import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Check, Circle, CircleDot } from "lucide-react";
import { CHAPTERS } from "@/tutorial/chapters";
import { readProgress } from "@/tutorial/progress";
import { cn } from "@/lib/utils";

/**
 * Sidebar of the Tutorial Center.
 *
 * - Lists the 11 chapter shells
 * - Shows the per-chapter completion status (✅ / 🔵 / ○) read from localStorage
 * - Highlights the active chapter (brand-blue background)
 * - Reacts live to completion changes via the `vtpTutorialProgress:change` event
 */
export default function TutorialSidebar({ currentId = null }) {
  const [progress, setProgress] = useState(() => readProgress());

  useEffect(() => {
    const refresh = () => setProgress(readProgress());
    // Same-tab updates (custom event dispatched by setChapterCompleted)
    window.addEventListener("vtpTutorialProgress:change", refresh);
    // Cross-tab updates
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("vtpTutorialProgress:change", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return (
    <aside
      className="h-full w-full overflow-y-auto border-r border-border bg-card px-3 py-4 scrollbar-sleek"
      data-testid="tutorial-sidebar"
    >
      <div className="px-2 pb-3">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          Sommaire
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {Object.values(progress).filter(Boolean).length} / {CHAPTERS.length} chapitres complétés
        </div>
      </div>

      <ul className="space-y-1" data-testid="tutorial-chapter-list">
        {CHAPTERS.map((ch) => {
          const isActive = String(ch.id) === String(currentId);
          const isDone = progress[String(ch.id)] === true;
          const num = String(ch.id).padStart(2, "0");
          return (
            <li key={ch.id}>
              <NavLink
                to={`/tutorial/chapter/${ch.id}`}
                data-testid={`tutorial-chapter-link-${ch.id}`}
                className={cn(
                  "group flex items-start gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                  isActive
                    ? "bg-brand-blue text-white shadow-sm"
                    : "text-foreground/85 hover:bg-accent/60 hover:text-foreground"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                    isActive
                      ? "bg-white/15 text-white"
                      : isDone
                      ? "bg-emerald-500 text-white"
                      : "bg-muted text-muted-foreground"
                  )}
                  data-testid={`tutorial-status-${ch.id}`}
                  aria-label={
                    isDone ? "Complété" : isActive ? "En cours" : "Non commencé"
                  }
                >
                  {isDone ? (
                    <Check className="h-3 w-3" />
                  ) : isActive ? (
                    <CircleDot className="h-3 w-3" />
                  ) : (
                    <Circle className="h-2.5 w-2.5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate font-mono text-[10px] uppercase tracking-widest",
                      isActive ? "text-white/70" : "text-muted-foreground"
                    )}
                  >
                    Chapitre {num}
                  </span>
                  <span className="block truncate font-medium leading-tight">
                    {ch.title}
                  </span>
                  <span
                    className={cn(
                      "mt-0.5 block text-[11px]",
                      isActive ? "text-white/75" : "text-muted-foreground"
                    )}
                  >
                    {ch.duration} min
                  </span>
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
